import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoMdArrowBack } from "react-icons/io";
import { 
  FaBookmark, FaRegBookmark, FaWhatsapp, FaPhoneAlt, 
  FaComments, FaShieldAlt, FaFlag, FaBan, FaTimes, FaCopy // Added FaCopy
} from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import * as api from "../../api/index";

const Viewproduct = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [product, setProduct] = useState(null);
  const [ownerdata, setOwnerdata] = useState(null);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [userId, setUserId] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserId(decoded.id || decoded._id);
      } catch (err) {
        console.error("Auth decode error");
      }
    }
  }, []);

  const fetchProduct = useCallback(async (currentUserId = userId) => {
    try {
      setLoading(true);
      setError("");
      
      const res = await api.Viewproduct(id, currentUserId);
      
      if (res?.data?.success) {
        setProduct(res.data.product);
        if (res.data.owner) setOwnerdata(res.data.owner);
        if (res.data.product.isBookmarked) setIsBookmarked(true);
      }
    } catch (err) {
      if (err.response && err.response.status === 403) {
        setError("This product is unavailable due to privacy or block settings.");
        setTimeout(() => navigate("/"), 3000);
      } else {
        setError("Failed to load product details.");
      }
    } finally {
      setLoading(false);
    }
  }, [id, userId, navigate]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    let currentId = null;
    if (token) {
      try {
        const decoded = jwtDecode(token);
        currentId = decoded.id || decoded._id;
      } catch (e) {}
    }
    fetchProduct(currentId);
  }, [id, fetchProduct]);

  // --- NEW: Handle Copy Product Link ---
  const handleCopyProductLink = () => {
    // This matches your deep linking config: view-product/:id
    const productUrl = `https://ourasap.com/view-product/${id}`;
    navigator.clipboard.writeText(productUrl)
      .then(() => alert("Product link copied! Ready to share."))
      .catch(() => alert("Failed to copy link."));
  };

  // ✅ ADDED: Block Product Logic (Matches Mobile)
  const handleBlockProduct = async () => {
    if (!userId) return navigate("/auth");

    const confirmBlock = window.confirm("Are you sure you want to block this product? You won't see this listing again.");
    if (!confirmBlock) return;

    try {
      const res = await api.Blockproductpost({ userId, productId: id });
      if (res?.data?.success || res?.status === 200) {
        alert("Product has been blocked.");
        navigate("/"); 
      }
    } catch (err) {
      alert("Failed to block product.");
    }
  };

  // ✅ ADDED: Report/Flag Product Logic (Matches Mobile)
  const handleFlagProduct = () => {
    if (!userId) return navigate("/auth");

    const confirmReport = window.confirm("Are you sure you want to report this product? Our team will review it for policy violations.");
    if (confirmReport) {
      alert("Thank you for looking out for our community. We have received your report and our moderators will review it within 24 hours.");
    }
  };

  const handleWhatsApp = () => {
    if (product?.phone) {
      const cleanNumber = product.phone.replace(/\D/g, "");
      const finalNumber = cleanNumber.startsWith('0') ? `234${cleanNumber.slice(1)}` : cleanNumber;
      window.open(`https://wa.me/${finalNumber}?text=${encodeURIComponent(`Hello, I saw your product "${product.name}" on OurASAP and I'm interested.`)}`, "_blank");
    }
  };

  const handleStartConversation = async () => {
    if (!userId) return navigate("/auth");
    if (userId === ownerdata?._id) return alert("Note: This is your own listing.");

    setConversationLoading(true);
    const dataOwner = {
      Name: ownerdata?.Name,
      Email: ownerdata?.Email,
      profileimage: ownerdata?.profileImage,
      _id: ownerdata?._id,
    };
    try {
      const res = await api.createConversation({
        senderId: userId,
        receiverId: product.userId?._id || product.userId,
      });
      if (res?.data?.conversation._id) {
        navigate(`/chat/${res.data.conversation._id}`, {
          state: { otherUser: dataOwner },
        });
      }
    } catch (err) {
      alert("Failed to start conversation.");
    } finally {
      setConversationLoading(false);
    }
  };

  const handleUseOurAsap = () => {
    if (!userId) return navigate("/auth");
    if (!product?.userId) return alert("⚠️ No registered user for this product.");
    navigate("/create-order", {
      state: { sellerId: product.userId._id || product.userId },
    });
  };

  const handleToggleBookmark = async () => {
    if (!userId) return navigate("/auth");
    setIsBookmarked((prev) => !prev);
    try {
      const res = await api.toggleBookmarkProduct({ userId, productId: id });
      if (!res?.data?.success) setIsBookmarked((prev) => !prev);
    } catch (err) {
      setIsBookmarked((prev) => !prev);
    }
  };

  if (loading) return <div className="flex justify-center items-center min-h-screen text-teal-600 font-medium">Loading product...</div>;
  if (error) return <p className="text-center mt-6 text-red-500 font-bold">{error}</p>;
  if (!product) return <p className="text-center mt-6 text-gray-500">Product not found.</p>;

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Top Navigation Bar */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full transition">
          <IoMdArrowBack size={24} className="text-gray-700" />
        </button>
        <h1 className="text-sm font-bold truncate max-w-[200px] text-gray-800">{product.name}</h1>
        
        {/* Actions Row: Copy & Bookmark */}
        <div className="flex items-center gap-2">
          <button onClick={handleCopyProductLink} className="p-2 hover:bg-gray-100 rounded-full transition text-gray-500">
            <FaCopy size={20} />
          </button>
          <button onClick={handleToggleBookmark} className="p-2 hover:bg-orange-50 rounded-full transition">
            {isBookmarked ? <FaBookmark size={22} className="text-orange-600" /> : <FaRegBookmark size={22} className="text-gray-400" />}
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 mt-4 space-y-6">
        
        {/* Modern Image Gallery */}
        <div className="bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100">
          <div className="grid grid-cols-4 grid-rows-2 gap-2 h-[350px] md:h-[500px]">
            <div className="col-span-3 row-span-2 relative group cursor-pointer" onClick={() => setSelectedImage(product.images?.[0])}>
              <img src={product.images?.[0]} className="w-full h-full object-cover" alt="Main" />
            </div>
            <div className="col-span-1 row-span-1 cursor-pointer" onClick={() => setSelectedImage(product.images?.[1] || product.images?.[0])}>
              <img src={product.images?.[1] || product.images?.[0]} className="w-full h-full object-cover" alt="Side 1" />
            </div>
            <div className="col-span-1 row-span-1 relative cursor-pointer" onClick={() => setSelectedImage(product.images?.[2] || product.images?.[0])}>
              <img src={product.images?.[2] || product.images?.[0]} className="w-full h-full object-cover" alt="Side 2" />
              {product.images?.length > 3 && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-bold">
                  +{product.images.length - 3}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-3xl font-extrabold text-gray-900">{product.name}</h2>
              <p className="text-2xl font-black text-teal-600 mt-2">
                ₦{product.price?.toLocaleString()}
                {product.negotiable && <span className="text-sm text-gray-400 font-normal ml-2 italic">(Negotiable)</span>}
              </p>

              <div className="flex flex-wrap gap-2 mt-6">
                {[product.category, product.subCategory, product.product].filter(Boolean).map((tag, i) => (
                  <span key={i} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-semibold uppercase tracking-wider">{tag}</span>
                ))}
              </div>

              <div className="mt-8">
                <h3 className="text-lg font-bold text-gray-800 border-b pb-2 mb-3">Description</h3>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">{product.description}</p>
              </div>
            </div>

            {/* Gallery Section */}
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold text-gray-800 mb-4">All Photos ({product.images?.length})</h3>
              <div className="grid grid-cols-3 gap-3">
                {product.images?.map((img, index) => (
                  <img key={index} src={img} onClick={() => setSelectedImage(img)} className="aspect-square w-full object-cover rounded-2xl cursor-pointer hover:ring-4 ring-teal-500/20 transition" alt={`Thumb ${index}`} />
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 sticky top-20">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Contact Seller</h3>
              {/* onClick={() => userId ? (window.location.href=`tel:${product.phone}`) : navigate("/auth")} */}
              <div className="space-y-3">
                {userId !== ownerdata?._id && (
                <button onClick={() => userId ? (window.location.href=`tel:${product.phone}`) : navigate("/auth")} className="w-full flex items-center justify-center gap-3 border-2 border-teal-600 text-teal-700 font-bold py-3 rounded-2xl hover:bg-teal-50 transition">
                  <FaPhoneAlt /> {userId ? product.phone || "Call Seller" : "View Number"}
                </button>
  )}
   {userId !== ownerdata?._id && (
                <button  onClick={() => userId ? handleWhatsApp() : navigate("/auth")} className="w-full flex items-center justify-center gap-3 border-2 border-green-500 text-green-600 font-bold py-3 rounded-2xl hover:bg-green-50 transition">
                  <FaWhatsapp size={20} /> WhatsApp
                </button>
  )}
                {userId !== ownerdata?._id && (
                  <button onClick={handleStartConversation} disabled={conversationLoading} className="w-full flex items-center justify-center gap-3 bg-teal-600 text-white font-bold py-3 rounded-2xl hover:bg-teal-700 transition disabled:opacity-50">
                    {conversationLoading ? <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" /> : <FaComments size={18}/>}
                    {conversationLoading ? "Initializing..." : "Message On OurASAP"}
                  </button>
                )}

                {userId !== ownerdata?._id && (
                  <button 
                    onClick={handleUseOurAsap} 
                    className="w-full bg-orange-600 text-white font-bold py-4 rounded-2xl hover:bg-orange-700 transition shadow-lg flex flex-col items-center"
                  >
                    <span>Pay & Deliver with OurASAP</span>
                    <span className="text-[10px] font-medium opacity-80 uppercase tracking-widest">Escrow Protected</span>
                  </button>
                )}
              </div>

              {/* Safety Tip */}
              <div className="mt-6 p-4 bg-teal-50 rounded-2xl border border-teal-100 flex gap-3">
                <FaShieldAlt className="text-teal-600 mt-1 shrink-0" size={20} />
                <p className="text-[12px] text-teal-900 leading-snug">
                  <strong>Safety Tip:</strong> Using <b>OurASAP Escrow</b> protects you from scams. Money is only released after you confirm delivery.
                </p>
              </div>

              {/* MODERATION SECTION */}
              <div className="mt-8 pt-6 border-t flex items-center justify-center gap-4">
                <button onClick={handleFlagProduct} className="flex items-center gap-2 text-gray-400 hover:text-gray-600 text-sm font-semibold transition">
                  <FaFlag size={14} /> Report
                </button>
                <div className="w-px h-4 bg-gray-200"></div>
                <button onClick={handleBlockProduct} className="flex items-center gap-2 text-red-400 hover:text-red-600 text-sm font-semibold transition">
                  <FaBan size={14} /> Block Post
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen Image Modal */}
      {selectedImage && (
        <div onClick={() => setSelectedImage(null)} className="fixed inset-0 bg-black/95 flex justify-center items-center z-[100] p-4 cursor-pointer">
          <button className="absolute top-6 right-6 text-white text-3xl"><FaTimes /></button>
          <img src={selectedImage} alt="Fullscreen" className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
};

export default Viewproduct;